import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import DoneAllSharpIcon from '@mui/icons-material/DoneAllSharp';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Box, Grid, ListItemButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  assignDncDetails,
  ColumnSettings,
  DncDetailsDTO,
  getColumnSettings,
  ILead,
  ILeadAPIResponse,
  ILeadSearchFilter,
  LogAction,
  LogContextType,
  saveActivityLog,
  updateLeadStatus,
} from 'wisestep-api';
import {
  FieldOption,
  WsBadge,
  WsBadgeSize,
  WsColors,
  WsDataGrid,
  WsDrawer,
  WsIconMoreVer,
  WsIconNoUserGeneric,
  WsIconText,
  WsListingLoader,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  formateDateInDateMonthYear,
  getDateUsingFormat,
  timeSince,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import openNotesFormDialog from '../../../features/clientDetails/helper/openNotesFormDialog';
import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import {
  defaultLeadListColumnSettings,
  saveColumnSettingsToDB,
} from '../../../features/clientListingPage/helper/ClientListingHelper';
import DataPrivacy from '../../../features/conversationWidget/components/DataPrivacy';
import WsLink from '../../../features/teams/components/WsLink';
import { useDialog } from '../../../hooks/useDialog';
import { LeadAccess, useLeadAuthz } from '../../../hooks/useLeadAuthz';
import ErrorLogger from '../../../providers/ErrorLogger';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  ITableLead,
  LeadUpdateDialog,
  StyledEventNoteIcon,
  defaultLeadActivityLogDTO,
  limitedPrivacyOptions,
  getLeadStatusByName,
} from '../helper/leadHelper';

import { LeadOwnerUpdateDialog, LeadStatusUpdateDialog } from './leadDetails';
import LeadInfoDialog from './LeadInfoDialog';

type SortModel = {
  field: string;
  sort: 'asc' | 'desc' | undefined;
}[];

interface ILeadProps {
  leads: ILeadAPIResponse;
  searchQuery: ILeadSearchFilter;
  getUpdatedFiltersFromTable: (data) => void;
  leadType: string;
  onSelectionChange?: (selectedIds: string[]) => void;
  onEditLead: (leadId: string) => void;
}

const LeadData = ({
  leads,
  searchQuery,
  getUpdatedFiltersFromTable,
  onEditLead,
  onSelectionChange,
}: ILeadProps) => {
  const [leadsData, setLeadsData] = useState<ILead[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [leadUpdateDialog, setLeadUpdateDialog] = useState<LeadUpdateDialog | null>(null);
  const [sortModel] = useState<SortModel>([
    {
      field: 'createdOn',
      sort: 'desc',
    },
  ]);
  const closeLeadUpdateDialog = () => setLeadUpdateDialog(null);
  const isAuthroizedEditLeadDetails = useLeadAuthz(LeadAccess.EDIT_LEAD_DETAILS);
  const [openDialog, closeDialog] = useDialog();
  const [selectedLead, setSelectedLead] = useState<ILead[]>([]);
  const [openDNC, setOpenDNC] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [settings, setSettings] = useState<FieldOption[]>(defaultLeadListColumnSettings);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);

  const setNotification = useNotificationStore((state) => state.setNotification);

  useEffect(() => {
    setLoading(true);
    setLeadsData(leads?.content);
    setLoading(false);
    setTotalElements(leads?.totalElements);
  }, [leads]);

  useEffect(() => {
    const fetchColumnSettings = async () => {
      try {
        const res = await getColumnSettings(ColumnSettings.LEAD_LISTING);
        const input = res.data?.settings;
        const columns: FieldOption[] = input?.column ?? [];
        setSettings(columns);
      } catch (error) {
        setSettings([
          { id: 'leadName', checked: true, required: true },
          { id: 'clientName', checked: true },
          { id: 'contactName', checked: true },
          { id: 'leadOwnerName', checked: true },
          { id: 'createdOn', checked: true },
          { id: 'tags', checked: true },
          { id: 'createdByName', checked: true },
          { id: 'status', checked: true },
          { id: 'source', checked: false },
          { id: 'lastActivity', checked: false },
          { id: 'edit', checked: true, required: true },
        ]);
      }
    };
    fetchColumnSettings();
  }, []);

  useEffect(() => {
    getUpdatedFiltersFromTable({
      ...searchQuery,
      sortBy: sortModel[0]?.field ?? 'createdOn',
      sortOrder: sortModel[0]?.sort ?? 'asc',
    });
  }, []);

  const handleLeadEditClick = (params) => onEditLead(params.id);

  const handleSave = async (updatedLeadData: ILead) => {
    try {
      if (leadUpdateDialog === LeadUpdateDialog.STATUS) {
        const currentLead = leadsData.find((l) => l.id === updatedLeadData.id);
        const oldStatus = currentLead ? currentLead.status : ' ';
        const newStatus = updatedLeadData.leadStatus?.leadStatusName;
        const logPayload = {
          ...defaultLeadActivityLogDTO,
          contextData: {},
          action: LogAction.Edit,
          field: 'Lead Status',
          description: `updated Lead Status from ${oldStatus} to ${newStatus} for ${updatedLeadData.leadName}`,
          entityId: updatedLeadData.id,
          entityName: updatedLeadData.leadName,
          parentEntitiyType: LogContextType.Lead,
          parentEntitiyId: updatedLeadData.id,
        };
        await saveActivityLog(logPayload);
      } else if (leadUpdateDialog === LeadUpdateDialog.OWNER) {
        const currentLead = leadsData.find((l) => l.id === updatedLeadData.id);
        const oldOwner = currentLead ? currentLead.leadOwnerInfo?.name : ' ';
        const newOwner = updatedLeadData.leadOwnerInfo?.name;
        const logPayload = {
          ...defaultLeadActivityLogDTO,
          contextData: {},
          action: LogAction.Edit,
          field: 'Lead Owner',
          description: `updated Lead Owner from ${oldOwner} to ${newOwner} for ${updatedLeadData.leadName}`,
          entityId: updatedLeadData.id,
          entityName: updatedLeadData.leadName,
          parentEntitiyType: LogContextType.Lead,
          parentEntitiyId: updatedLeadData.id,
        };
        await saveActivityLog(logPayload);
      }

      closeLeadUpdateDialog();
      getUpdatedFiltersFromTable({ ...searchQuery });
    } catch (error) {
      console.error('Error updating lead owner:', error);
    }
  };

  const renderLinkTypography = (href: string, text: string) => (
    <WsLink to={href}>
      <WsTypography variant={WsTypoVariants.SUB_TITLE}>{text}</WsTypography>
    </WsLink>
  );

  const encodedQuery = getEncodedQueryParams({
    ...searchQuery,
    fromDate: searchQuery.fromDate ? new Date(searchQuery.fromDate).toISOString() : '',
    toDate: searchQuery.toDate ? new Date(searchQuery.toDate).toISOString() : '',
  });

  const columnsName: GridColDef[] = [
    {
      field: 'leadName',
      headerName: 'Lead Name',
      flex: 2,
      renderCell: (params) => {
        const leadInfo = leadsData?.[params.row.rowIndex];
        return (
          <Grid container direction="row" alignItems="center" justifyContent={'space-between'}>
            <Grid item>
              {renderLinkTypography(
                `/leads/details?leadId=${params.row.id}&d=${encodedQuery}`,
                params.row['leadName']
              )}
            </Grid>
            <Grid item xs={3}>
              <LeadInfoDialog leadInfo={leadInfo} />
            </Grid>
          </Grid>
        );
      },
    },
    {
      field: 'clientName',
      headerName: 'Client Name',
      flex: 1.5,
      renderCell: (params) => {
        const client = leadsData?.[params.row.rowIndex]?.client;
        const isChild = client?.parentId == null;
        return renderLinkTypography(
          `/client-details?clientId=${client?.id}&isChild=${isChild}&tabIndex=0`,
          params.row['clientName']
        );
      },
    },
    {
      field: 'contactName',
      headerName: 'Contact Name',
      flex: 1.5,
      renderCell: (params) => {
        const lead = leadsData?.[params.row.rowIndex];
        return renderLinkTypography(
          `/${lead?.clientContact?.id}/client-contact-details?isChild=true&name=${lead?.clientContact?.firstName}&goBack=true&clientId=${lead?.client?.id}`,
          params.row['contactName']
        );
      },
    },
    {
      field: 'leadOwnerName',
      headerName: 'Lead Owner',
      flex: 2,
      renderCell: (params) => (
        <WsLink
          to={`/users/details-page?email=${params.row['leadOwnerId']}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            {params.row['leadOwnerName']}
          </WsTypography>
        </WsLink>
      ),
    },
    { field: 'tags', headerName: 'Tags', flex: 1, sortable: false },
    {
      field: 'createdByName',
      headerName: 'Created By',
      flex: 1,
      renderCell: (params) => (
        <WsLink
          to={`/users/details-page?email=${params.row['createdBy']}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            {params.row['createdByName']}
          </WsTypography>
        </WsLink>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Date Created',
      flex: 1,
    },
    {
      field: 'source',
      headerName: 'Source',
      flex: 1,
      hide: true,
    },
    {
      field: 'lastActivity',
      headerName: 'Last Activity',
      flex: 1,
      hide: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.5,
      renderCell: (params) => {
        const lead = leadsData?.[params.row.rowIndex];
        return (
          <Box>
            <Grid container marginTop={1}>
              {/* <StyledChip
                label={lead.leadStatus?.leadStatusName}
                mt={8}
                textColor={WsColors.GenericRichBlack}
                bgColor={WsColors.SkyBlueTint}
                fontWeight={500}
                borderRadius={15}
                height={20}
              /> */}
              <WsBadge
                label={lead.leadStatus?.leadStatusName ?? ''}
                badgeSize={WsBadgeSize.MEDIUM}
              />
            </Grid>
            <Grid container marginLeft={1} gap={1} marginBottom={1}>
              <WsIconText
                icon={
                  <AccessTimeOutlinedIcon
                    sx={{ color: WsColors.TertiaryShades800, fontSize: 14 }}
                  />
                }
                text={
                  (lead?.modifiedOn &&
                    (new Date().getTime() - new Date(lead?.modifiedOn).getTime() <
                    24 * 60 * 60 * 1000
                      ? timeSince(lead?.modifiedOn)
                      : formateDateInDateMonthYear(lead?.modifiedOn))) ??
                  'Invalid Date'
                }
                textSize={WsTypoVariants.BODY1}
                textCursor="text"
                textColor={WsTypoColors.QUINARY}
                popOverText={`Last Updated ${formateDateInDateMonthYear(lead?.modifiedOn ?? '')}`}
              />
            </Grid>
          </Box>
        );
      },
    },
    {
      field: 'edit',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const lead = leadsData?.[params.row.rowIndex];
        const isInDnc = params.row.dncDetails?.isInDnc;
        return (
          <PopoverOnClick element={<WsIconMoreVer />}>
            <ListItemButton>
              <WsIconText
                text="Add Note"
                textCursor="pointer"
                icon={<StyledEventNoteIcon />}
                textColor={WsTypoColors.BLACK}
                onClick={() => {
                  openNotesFormDialog({
                    clientId: lead?.client?.id ?? '',
                    contactId: lead.clientContact?.id ?? '',
                    clientName: lead.client?.name,
                    contactName: lead.clientContact?.firstName,
                    isClient: false,
                    openDialog: openDialog,
                    closeDialog: closeDialog,
                  });
                }}
              />
            </ListItemButton>
            {isAuthroizedEditLeadDetails && (
              <>
                <ListItemButton>
                  <WsIconText
                    text="Update Status"
                    textCursor="pointer"
                    icon={
                      <DoneAllSharpIcon
                        sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }}
                      />
                    }
                    textColor={WsTypoColors.BLACK}
                    onClick={() => setLeadUpdateDialog(LeadUpdateDialog.STATUS)}
                  />
                </ListItemButton>
                <ListItemButton>
                  <WsIconText
                    text="Change Lead Owner"
                    textCursor="pointer"
                    icon={
                      <PersonAddAlt1Icon
                        sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }}
                      />
                    }
                    textColor={WsTypoColors.BLACK}
                    onClick={() => setLeadUpdateDialog(LeadUpdateDialog.OWNER)}
                  />
                </ListItemButton>
                <ListItemButton>
                  <WsIconText
                    text="Edit Lead"
                    textCursor="pointer"
                    icon={<EditIcon sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }} />}
                    textColor={WsTypoColors.BLACK}
                    onClick={() => handleLeadEditClick(params)}
                  />
                </ListItemButton>
                {!isInDnc && (
                  <ListItemButton>
                    <WsIconText
                      text="Mark DNC"
                      textCursor="pointer"
                      icon={
                        <DoDisturbOnIcon
                          sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }}
                        />
                      }
                      textColor={WsTypoColors.BLACK}
                      onClick={() => {
                        setOpenDNC(!openDNC);
                        setSelectedLead(lead);
                      }}
                    />
                  </ListItemButton>
                )}
              </>
            )}
            {leadUpdateDialog == LeadUpdateDialog.STATUS && (
              <LeadStatusUpdateDialog
                onSave={handleSave}
                onCancel={closeLeadUpdateDialog}
                lead={lead}
              />
            )}
            {leadUpdateDialog == LeadUpdateDialog.OWNER && (
              <LeadOwnerUpdateDialog
                onSave={handleSave}
                onCancel={closeLeadUpdateDialog}
                lead={lead}
              />
            )}
          </PopoverOnClick>
        );
      },
    },
  ];

  const onColumnSettingsReset = () => {
    saveColumnSettingsToDB(
      defaultLeadListColumnSettings(),
      ColumnSettings.LEAD_LISTING,
      setNotification
    );

    setSettings(defaultLeadListColumnSettings);
  };

  const tableData: ITableLead[] = leadsData?.map((lead, index) => ({
    id: lead.id,
    leadName: lead.leadName,
    clientName: lead.client?.name,
    contactName: lead.clientContact
      ? `${lead.clientContact?.firstName} ${lead.clientContact?.lastName ?? ''}`
      : '',
    businessUnitsNames: lead.businessUnitsInfo?.buinfo?.map((bu) => bu.name).join(', ') || '',
    orgName: lead.organisationEntityName,
    leadOwnerName: lead.leadOwnerInfo?.name ?? lead.leadOwnerInfo?.id ?? '',
    leadOwnerId: lead.leadOwnerInfo?.id ?? '',
    createdOn:
      lead?.createdOn != undefined
        ? getDateUsingFormat(lead.createdOn?.toString(), 'DD-MMM-YY')
        : '',
    createdByName: lead.createdByName,
    createdBy: lead.createdBy,
    status: lead?.leadStatus?.leadStatusName ?? '',
    source: lead.source,
    description: lead.description ?? '',
    linkedinUrl: lead.linkedinUrl ?? '',
    tags: lead.tags ?? [],
    dncDetails: lead.clientContact?.dncDetails,
    rowIndex: index,
    lastActivity: lead.lastActivity,
  }));

  const handleDncSubmit = async (leadId: string, contactId: string, data: DncDetailsDTO) => {
    try {
      const leadStatus = await getLeadStatusByName('Closed');
      await assignDncDetails(contactId, data);
      if (data.isInDnc) {
        await updateLeadStatus({
          id: leadId,
          leadName: '',
          source: '',
          leadStatus: { id: leadStatus.id },
        } as ILead);
      }
    } catch (error) {
      console.error('Error updating DNC details / lead status:', error);
    }
  };
  const onSortModelChange = () => {
    setPageNo(0);
  };
  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      {loading ? (
        <WsListingLoader />
      ) : (
        <>
          {isEmpty(tableData) ? (
            <NoDataFoundCard
              text="No leads to show for applied filter(s)"
              icon={<WsIconNoUserGeneric />}
              subElement={<WsTypography>No Leads</WsTypography>}
            />
          ) : (
            <>
              {settings != null && (
                <WsDataGrid
                  checkboxSelection
                  selectionModel={selectedLeadIds}
                  onSelectionModelChange={(newSelection) => {
                    setSelectedLeadIds(newSelection as string[]);
                    onSelectionChange?.(newSelection as string[]);
                  }}
                  totalRows={totalElements}
                  autoHeight
                  columns={columnsName}
                  rows={tableData ?? []}
                  page={pageNo}
                  pageSize={pageSize}
                  onSortModelChange={onSortModelChange}
                  columnVisibility
                  columnSettings={settings}
                  onColumnSettingsChange={(data) => {
                    saveColumnSettingsToDB(data, ColumnSettings.LEAD_LISTING, setNotification);
                    setSettings(data);
                  }}
                  onColumnSettingsReset={onColumnSettingsReset}
                  onPageSizeChange={setPageSize}
                  onPageChange={setPageNo}
                />
              )}
            </>
          )}
          {openDNC && selectedLead && (
            <WsDrawer open={openDNC} onClose={() => setOpenDNC(!openDNC)}>
              <DataPrivacy
                clientContactParentId={selectedLead?.clientContact?.clientId}
                clientContactId={selectedLead.clientContact?.id}
                clientContactName={selectedLead.clientContact?.firstName}
                email={selectedLead?.clientContact?.createdBy ?? ''}
                candidateName={selectedLead?.leadName ?? ''}
                isInDnc={selectedLead?.clientContact?.dncDetails?.isDnc ?? false}
                dncDetails={selectedLead?.clientContact?.dncDetails}
                privacyOptions={limitedPrivacyOptions}
                restrictLabel="DNC Up to*"
                entityId={selectedLead?.id}
                parentEntitiyId={selectedLead?.id ?? ''}
                entityName={selectedLead?.leadName}
                logContextType={LogContextType.Lead}
                onSubmitApi={(data) =>
                  handleDncSubmit(selectedLead.id, selectedLead.clientContact?.id, data)
                }
                enableActivityLog={true}
              />
            </WsDrawer>
          )}
        </>
      )}
    </ErrorBoundary>
  );
};

export default LeadData;
